import { useEffect, useState } from "react";
import {
  Building2, Bus, Plane, BadgeCheck, UtensilsCrossed, Ticket, ShoppingBag, Route, Headset, ArrowRight,
} from "lucide-react";
import SEO from "../lib/seo";
import PageHero from "../components/PageHero";
import SectionHeading from "../components/SectionHeading";
import PackageCard from "../components/PackageCard";
import CtaSection from "../components/CtaSection";
import { Reveal } from "../components/Reveal";
import { getPackages } from "../lib/api";
import { useLead } from "../lib/store";
import { track } from "../lib/track";

const HERO_IMG =
  "https://images.unsplash.com/photo-1508804185872-d7badad00f7d?crop=entropy&cs=srgb&fm=jpg&q=80&auto=format&fit=crop&w=1800";

const SERVICES = [
  { icon: Building2, title: "Hotel", desc: "Akomodasi sesuai kategori pilihan Anda, dari 3 star hingga 5 star." },
  { icon: Bus, title: "Transport", desc: "Private van & bus full AC dengan driver berpengalaman." },
  { icon: Plane, title: "Airport Transfer", desc: "Penjemputan dan pengantar bandara tanpa ribet." },
  { icon: BadgeCheck, title: "Guide", desc: "Local guide berpengalaman, tersedia bahasa Indonesia/Inggris." },
  { icon: UtensilsCrossed, title: "Meals", desc: "Makan sesuai program, termasuk opsi halal dan muslim-friendly." },
  { icon: Ticket, title: "Attraction", desc: "Tiket objek wisata dan atraksi tanpa antre." },
  { icon: ShoppingBag, title: "Shopping", desc: "Rute belanja oleh-oleh dan kuliner lokal yang terkurasi." },
  { icon: Route, title: "Custom Itinerary", desc: "Program perjalanan dirancang ulang sesuai kebutuhan Anda." },
  { icon: Headset, title: "Local Support", desc: "Tim lokal siap membantu selama perjalanan berlangsung." },
];

export default function LandArrangement() {
  const [packages, setPackages] = useState([]);
  const { openLead } = useLead();

  useEffect(() => {
    getPackages().then(setPackages).catch(() => {});
  }, []);

  return (
    <>
      <SEO
        title="Land Arrangement Internasional — Your Destination, Our Arrangement | Beyond Travel"
        description="Layanan Land Arrangement: hotel, transport, airport transfer, guide, meals, attraction ticket, custom itinerary, dan local support di negara tujuan."
      />
      <PageHero
        image={HERO_IMG}
        eyebrow="Land Arrangement"
        title="Your Destination, Our Arrangement"
        sub="Kami membantu menyiapkan kebutuhan perjalanan Anda di negara tujuan, dari airport transfer hingga itinerary dan local support."
        crumb="Land Arrangement"
      >
        <button
          onClick={() => {
            track("request_quotation", { source: "la_hero" });
            openLead({ source: "land-arrangement" });
          }}
          className="btn btn-gold !px-8 !py-3.5"
          data-testid="la-hero-request-button"
        >
          Request Land Arrangement
        </button>
      </PageHero>

      <section className="section bg-white">
        <div className="container-x grid items-center gap-12 lg:grid-cols-2">
          <div>
            <SectionHeading
              eyebrow="Apa itu Land Arrangement?"
              title="Beli Layanan Perjalanan di Negara Tujuan, Tanpa Paket Kaku"
            />
            <Reveal delay={0.15}>
              <p className="mt-5 text-sm leading-relaxed text-slate-600 sm:text-base">
                Land Arrangement (LA) adalah layanan pengaturan perjalanan Anda di negara tujuan: itinerary, hotel, transportasi lokal, guide, tiket objek wisata, makan, airport transfer, dan layanan perjalanan lainnya sesuai paket.
              </p>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-4 text-sm leading-relaxed text-slate-600 sm:text-base">
                Anda bebas mengatur penerbangan dan jadwal sendiri, sementara seluruh kebutuhan di daratan ditangani tim kami — fleksibel untuk private trip, keluarga, hingga group besar.
              </p>
            </Reveal>
            <Reveal delay={0.25}>
              <button
                onClick={() => openLead({ source: "land-arrangement-explainer" })}
                className="btn btn-primary mt-7"
                data-testid="la-explainer-request-button"
              >
                Request Land Arrangement <ArrowRight className="h-4 w-4" />
              </button>
            </Reveal>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            {SERVICES.slice(0, 4).map((s, i) => (
              <Reveal key={s.title} delay={i * 0.08} className={i % 2 === 1 ? "sm:mt-8" : ""}>
                <div className="rounded-3xl border border-slate-100 bg-cream p-6 card-hover">
                  <s.icon className="h-7 w-7 text-tosca-600" />
                  <h3 className="mt-4 font-heading text-base font-bold text-charcoal">{s.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-500">{s.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="section bg-cream" data-testid="la-services">
        <div className="container-x">
          <SectionHeading
            eyebrow="Complete Services"
            title="Semua Kebutuhan di Negara Tujuan"
            sub="Kombinasikan layanan berikut sesuai paket dan kebutuhan perjalanan Anda."
          />
          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {SERVICES.map((s, i) => (
              <Reveal key={s.title} delay={Math.min(i * 0.05, 0.3)} className="h-full">
                <div className="h-full rounded-3xl bg-white p-6 shadow-crystal card-hover" data-testid={`la-service-${s.title.toLowerCase().replace(/ /g, "-")}`}>
                  <s.icon className="h-7 w-7 text-tosca-600" />
                  <h3 className="mt-4 font-heading text-base font-bold text-charcoal">{s.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-500">{s.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="section bg-white" data-testid="la-packages">
        <div className="container-x">
          <SectionHeading
            eyebrow="Packages"
            title="Land Arrangement Packages"
            sub="Paket per destinasi — seluruh itinerary dapat disesuaikan. Hubungi kami untuk harga terbaru."
          />
          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {packages.map((p, i) => (
              <PackageCard key={p.slug} pkg={p} index={i} />
            ))}
          </div>
        </div>
      </section>

      <CtaSection
        title="Siap Menyusun Land Arrangement Anda?"
        sub="Kirimkan kebutuhan perjalanan Anda dan tim kami akan menyiapkan proposal serta quotation."
      />
    </>
  );
}
